import React from 'react';
import { Box, Chip, IconButton, Paper, Typography } from '@mui/material';
import { ChevronRight as ChevronRightIcon, Description as DescriptionIcon, ContentPaste as PasteIcon } from '@mui/icons-material';
import { colors, typography } from '../../../shared/styles/theme';
import type { SessionListItem } from '../types/contracts';

export type ContractReviewSubmissionListProps = {
  sessions: SessionListItem[];
  onOpen: (sessionUuid: string) => void;
};

const ICON_BG = 'rgba(72, 161, 173, 0.16)';

function sessionTitle(s: SessionListItem): string {
  return s.contract_type ? `${s.client_name} – ${s.contract_type}` : s.client_name;
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

const ContractReviewSubmissionList: React.FC<ContractReviewSubmissionListProps> = ({ sessions, onOpen }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    {sessions.map((session) => (
      <Paper
        key={session.session_uuid}
        elevation={0}
        onClick={() => onOpen(session.session_uuid)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          p: 2,
          borderRadius: 2,
          backgroundColor: colors.background.card,
          cursor: 'pointer',
          border: `1px solid ${colors.border.light}`,
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
          transition: 'background-color 0.15s, border-color 0.15s, box-shadow 0.15s',
          '&:hover': {
            backgroundColor: colors.background.hover,
            borderColor: colors.border.medium,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              flexShrink: 0,
              backgroundColor: ICON_BG,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {session.is_pasted
              ? <PasteIcon sx={{ fontSize: 22, color: colors.primaryLight }} />
              : <DescriptionIcon sx={{ fontSize: 22, color: colors.primaryLight }} />
            }
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: typography.fontWeight.medium,
                color: '#111827',
                fontSize: typography.fontSize.md,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {sessionTitle(session)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
              <Typography sx={{ color: colors.text.secondary, fontSize: typography.fontSize.sm }}>
                {session.total_clauses} clause{session.total_clauses === 1 ? '' : 's'} · {formatDate(session.created_at)}
              </Typography>
              <Chip
                label={session.is_signed ? 'Signed' : 'Draft'}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  backgroundColor: session.is_signed ? 'rgba(46, 125, 50, 0.1)' : 'rgba(21, 108, 134, 0.1)',
                  color: session.is_signed ? '#2e7d32' : colors.primary,
                  border: `1px solid ${session.is_signed ? 'rgba(46, 125, 50, 0.3)' : 'rgba(21, 108, 134, 0.3)'}`,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
              <Chip
                label={session.is_pasted ? 'Pasted' : 'Uploaded'}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  backgroundColor: session.is_pasted ? 'rgba(107, 70, 193, 0.08)' : 'rgba(72, 161, 173, 0.08)',
                  color: session.is_pasted ? '#6b46c1' : colors.primaryLight,
                  border: `1px solid ${session.is_pasted ? 'rgba(107, 70, 193, 0.25)' : 'rgba(72, 161, 173, 0.25)'}`,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <IconButton
            size="small"
            aria-label="Open submission"
            onClick={(e) => {
              e.stopPropagation();
              onOpen(session.session_uuid);
            }}
            sx={{ color: colors.primaryLight }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
    ))}
  </Box>
);

export default ContractReviewSubmissionList;
